import { apiClient } from '../apiHelper';
import { useSingleFetch } from '../api/react-query';

/**
 * The signed-in user's effective "entity.action" permission set, fetched from the
 * dedicated self-endpoint (keeps /users/me unchanged). Used for UI gating only —
 * the backend enforces every action regardless of what the UI shows.
 */
export function useMyPermissions(config = {}) {
  const fn = async (): Promise<string[]> => {
    const res = await apiClient.get('/users/me/permissions');
    // success_response → body.data.permissions
    return ((res as any)?.data?.permissions ?? (res as any)?.permissions ?? []) as string[];
  };
  return useSingleFetch(fn, ['me-permissions'], undefined, { retry: 0, ...config });
}

/**
 * `can(required)` — OR semantics: passes if the user holds ANY of the listed
 * permissions. Empty / undefined requirement = no gate (any signed-in user).
 */
export function usePermissions() {
  const q = useMyPermissions();
  const perms = new Set<string>((q?.data as string[]) ?? []);

  const can = (required?: string | string[]): boolean => {
    if (!required || (Array.isArray(required) && required.length === 0)) return true;
    const list = Array.isArray(required) ? required : [required];
    return list.some((p) => perms.has(p));
  };

  return { perms, can, isLoading: q.isLoading, ready: !q.isLoading && q.data !== undefined };
}
